import { ElementButton } from 'macaron-frontend-ui'
import { useEffect } from 'react'
import { useEnv } from '~/env'
import { useNow } from '~/lib/date/NowProvider'
import { format } from '~/lib/date/dateTime'
import { ClientError } from '~/lib/error/clientError'
import { isUnsupportedIPhone } from '~/lib/error/ua'
import { urlRedirectToYahooLogin } from '~/lib/member/yahoo'
import { css } from '~/styled-system/css'
import { vstack } from '~/styled-system/patterns'
import { DangerousHtml } from '../dangerousHtml/dangerousHtml'

type Props = {
  error: any
}

const oldIphone = isUnsupportedIPhone()

const errorBoxClass = vstack({
  gap: '2',
  alignContent: 'center',
  border: 'light',
  borderColor: 'error.border',
  p: '4',
  w: 'full',
  md: { w: 'auto' },
})

function ErrorBody({ error }: Props) {
  const env = useEnv()
  const now = useNow()

  // googlebot (chromium) でこのエラー画面が表示されたとき（コース取得などで 500 エラーが出たときなど）、
  // エラー画面がインデックスされないように meta タグで noindex を書き換える
  // 問題はエラーが発生したときの店舗がしばらく noindex になってしまうこと...
  // https://developers.google.com/search/docs/crawling-indexing/javascript/fix-search-javascript?hl=ja
  useEffect(() => {
    for (const e of document.querySelectorAll('meta[name="robots"]')) {
      e.remove()
    }
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.prepend(meta)
  }, [])

  return (
    <>
      <div className={vstack({ gap: '5', flex: '1' })}>
        <div className={errorBoxClass}>
          {error instanceof ClientError ? (
            <>
              <h2 className="text-xl font-bold">エラー</h2>
              {error.content.type === 'string' && (
                <p className={css({ color: 'error.text', whiteSpace: 'pre-wrap' })}>
                  {error.content.message}
                </p>
              )}
              {error.content.type === 'html' &&
                error.content.fragments.map((fragment, index) => (
                  <DangerousHtml
                    className={css({ color: 'error.text', whiteSpace: 'pre-wrap' })}
                    fragment={fragment}
                    key={index}
                  />
                ))}
              {error.message.includes('E-RS-C20009') &&
                error.message.includes('アクセストークン') && (
                  <p>
                    ログイン情報の有効期限が切れています。お手数ですが、
                    <a className="text-accent-blue underline" href={urlRedirectToYahooLogin(env)}>
                      再度ログイン
                    </a>
                    の上、お試しください。
                  </p>
                )}
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold">システムエラー</h2>
              <p className={css({ color: 'error.text', textAlign: 'left' })}>
                一時的に操作ができない状態になっています。
                <br />
                お手数ですが、しばらく時間を置いてから再度操作をお試しください。
              </p>
              {oldIphone && (
                <p className="mt-2 text-left text-neutral-700">
                  ご利用の環境が
                  <a
                    href="https://www.ikyu.com/help/ex/security.htm"
                    className="text-accent-blue underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    推奨のバージョン
                  </a>
                  より古いために発生している可能性がありますので、
                  <a
                    href="https://support.apple.com/ja-jp/HT204204"
                    className="text-accent-blue underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ソフトウェアアップデート
                  </a>
                  の上でお試しいただきますようお願いいたします。
                </p>
              )}
            </>
          )}
          <p className="text-neutral-600">日時：{format(now, 'yyyy年MM月dd日HH:mm:ss')}</p>
        </div>
        <div className={css({ w: 'full', md: { w: 'auto' } })}>
          <ElementButton
            color="tertiary"
            withSpread={true}
            label="戻る"
            onClick={() => location.reload()}
          />
        </div>
      </div>
      <a href="/" className={css({ display: 'block', flex: 'none', color: 'link' })}>
        トップページへ戻る
      </a>
    </>
  )
}

export default ErrorBody
